<template>
  <div class="container has-text-centered" v-if="camera">
    <h4 class="title is-4">{{camera.name}}</h4>
    <div class="container">
      <div v-if="topicValue" class="image_container">
        <img :src="urlSnapshot" @load="loadHandler" @error="errorHandler" :style="imageStyle" class="image"/>
      </div>
      <div v-else>No snapshot path provided.</div>
    </div>
    <div class="container">
      <div v-if="topicForLastMotion" class="image_last_motion">
        <span>Last motion: {{lastMotionValue}}</span>
      </div>
      <div v-if="topicForSnapshotRating" class="image_rating">
        <span>Snapshot rating: {{snapshotRatingValue}} %</span>
      </div>
      <div v-if="topicForMotionRating" class="image_rating">
        <span>Motion rating: {{motionRatingValue}} %</span>
      </div>
      <div class="toggler">
        <b-switch v-model="isMotionCycle" @input="cycleMotionChanged">Cycle cameras at motion</b-switch>
      </div>
    </div>
  </div>
  <div v-else>
    <h4>Waiting for camera...</h4>
  </div>
</template>

<script>
  import moment from 'moment';
  import {mapGetters, mapMutations, mapActions} from 'vuex';
  import {formatTime} from "../utils/utils";
  import BSwitch from "buefy/src/components/switch/Switch";

  export default {
    components: { BSwitch },
    name: "screencameraview",
    props: ['id'],
    data() {
      return {
        camera: null,
        topicForSnapshot: null,
        topicForSnapshotInterval: null,
        topicForLastMotion: null,
        topicForSnapshotRating: null,
        topicForMotionRating: null,
        intervalFetchSnapshot: undefined,
        secondsBetweenFetches: 1,
        rand: Math.floor(new Date().getTime() / 1000),
        isMotionCycle: false,
        generalLastMotionId: 0,
        generalLastMotionTimestamp: 0,
        isFailed: false,
        lastLoad: 0,
        intervalCheckLastLoad: undefined,
        secondsBetweenCheckingLastLoad: 3
      };
    },
    watch: {
      serialized: {
        immediate: true,
        handler() {
          if (this.isMotionCycle) {
            const generalLastMotionId = this.mapping['live/cameras/lastMotionId'];
            const generalLastMotionTimestamp = this.mapping['live/cameras/lastMotionTimestamp'];
            if (generalLastMotionTimestamp > this.generalLastMotionTimestamp) {
              if (this.generalLastMotionId !== generalLastMotionId) {
                this.camera = this.cameraById(generalLastMotionId);
                this.refreshTopics();
              } else {
                this.refreshTopics();
              }
            }
          }
        }
      }
    },
    computed: {
      ...mapGetters('cameras', ['cameraById', 'motionCycle']),
      ...mapGetters('mqtt', ['valueForTopic', 'serialized', 'mapping']),
      ...mapGetters('ui', ['windowSizes', 'cameraViewSnapshotInterval']),
      topicValue() {
        return this.topicForSnapshot ? this.valueForTopic(this.topicForSnapshot) : null;
      },
      cameraSnapshotInterval() {
        if (this.isFailed) {
          return this.secondsBetweenFetches * 10;
        }

        return this.topicForSnapshotInterval ? this.valueForTopic(this.topicForSnapshotInterval) : this.secondsBetweenFetches;
      },
      lastMotionValue() {
        const timestampValue = this.topicForLastMotion ? this.valueForTopic(this.topicForLastMotion) : 0;

        return timestampValue > 0 ? formatTime(timestampValue) : '-';
      },
      snapshotRatingValue() {
        return this.topicForSnapshotRating ? this.valueForTopic(this.topicForSnapshotRating) : '-';
      },
      motionRatingValue() {
        return this.topicForMotionRating ? this.valueForTopic(this.topicForMotionRating) : '-';
      },
      urlSnapshot() {
        return `${this.topicValue}?r=${this.rand}`;
      },
      imageWidth() {
        const w = this.windowSizes['width'];

        if (w > 1280) {
          return 1280;
        } else if (w > 1024) {
          return 1024;
        } else if (w > 768) {
          return 768;
        } else if (w >= 500) {
          return 480;
        } else if (w >= 425) {
          return 400;
        } else if (w >= 375) {
          return 340;
        }

        return 280;
      },
      imageHeight() {
        return this.windowSizes['height'];
      },
      imageStyle() {
        return `width: ${this.imageWidth}px;`;
      }
    },
    methods: {
      ...mapMutations('cameras', ['SET_MOTION_CYCLE']),
      ...mapActions('cameras', ['fetchSnapshot']),
      loadHandler() {
        this.lastLoad = Math.floor(new Date().getTime() / 1000);

        if (this.isFailed) {
          this.isFailed = false;

          if (typeof this.intervalFetchSnapshot !== 'undefined') {
            clearInterval(this.intervalFetchSnapshot);

            this.intervalFetchSnapshot = setInterval(() => {
              this.rand = moment().format('X');
            }, this.cameraSnapshotInterval * 1000);
          }
        }

        if (typeof this.intervalFetchSnapshot === 'undefined') {
          this.intervalFetchSnapshot = setInterval(() => {
            this.rand = Math.floor(new Date().getTime() / 1000);
          }, this.cameraSnapshotInterval * 1000);
        }
      },
      errorHandler() {
        this.isFailed = true;

        if (typeof this.intervalFetchSnapshot !== 'undefined') {
          clearInterval(this.intervalFetchSnapshot);

          this.intervalFetchSnapshot = setInterval(() => {
            this.rand = moment().format('X');
          }, this.cameraSnapshotInterval * 1000);
        }
      },
      cycleMotionChanged() {
        this['SET_MOTION_CYCLE'](this.isMotionCycle);
      },
      refreshTopics() {
        if (this.camera && this.camera.topics) {
          const that = this;

          this.camera.topics.forEach(item => {
            if (item.name === "snapshotUrl") {
              that.topicForSnapshot = item.topic;
            } else if (item.name === "lastMotion") {
              that.topicForLastMotion = item.topic;
            } else if (item.name === "ratingSnapshot") {
              that.topicForSnapshotRating = item.topic;
            } else if (item.name === "ratingMotion") {
              that.topicForMotionRating = item.topic;
            }
          });
        }
      },
      checkLastLoad() {
        const timestamp = Math.floor(new Date().getTime() / 1000);
        if (timestamp - this.lastLoad > this.cameraViewSnapshotInterval * 2) {
          this.secondsBetweenFetches += 1;
          this.errorHandler();
        }
      }
    },
    mounted() {
      this.camera = this.cameraById(this.id);
      this.refreshTopics();
      this.isMotionCycle = this.motionCycle;
      this.generalLastMotionTimestamp = Math.floor(new Date().getTime() / 1000);
      this.secondsBetweenFetches = this.cameraViewSnapshotInterval;
      this.lastLoad = Math.floor(new Date().getTime() / 1000);
      this.intervalCheckLastLoad = setInterval(() => {
        this.checkLastLoad();
      }, this.secondsBetweenCheckingLastLoad * 1000);
    },
    unmounted() {
      if (typeof this.intervalFetchSnapshot !== 'undefined') {
        clearInterval(this.intervalFetchSnapshot);
      }
      if (typeof this.intervalCheckLastLoad !== "undefined") {
        clearInterval(this.intervalCheckLastLoad);
      }
    },
    destroyed() {
      if (typeof this.intervalFetchSnapshot !== 'undefined') {
        clearInterval(this.intervalFetchSnapshot);
      }
      if (typeof this.intervalCheckLastLoad !== "undefined") {
        clearInterval(this.intervalCheckLastLoad);
      }
    }
  }
</script>

<style scoped>
  .image_container {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .image {
    border: solid 1px #000000;
  }

  .image_last_motion {
    margin-top: 1.25rem;
    margin-bottom: 1.25rem;
  }

  .image_rating {
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
    font-size: 0.75rem;
  }

  .toggler {
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
  }
</style>